import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const FOCUS_AREAS = [
  "Cleaning",
  "Delivery",
  "Material Movement",
  "Manufacturing",
  "Landscaping",
  "Customer Service",
  "Administrative Work",
  "Facility Operations",
  "Other",
];

const STEPS = [
  { key: "N", label: "Name the Need" },
  { key: "E", label: "Evaluate the Environment" },
  { key: "I", label: "Itemize the Inputs" },
  { key: "L", label: "Lock the Labor Baseline" },
  { key: "✓", label: "Contact" },
];

const contactSchema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(120),
  email: z.string().trim().email("Please enter a valid email"),
  phone: z.string().trim().max(40).optional(),
  business_name: z.string().trim().max(200).optional(),
});

const NeilAssessment = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [focus, setFocus] = useState("");
  const [need, setNeed] = useState("");
  const [environment, setEnvironment] = useState("");
  const [facilitySize, setFacilitySize] = useState("");
  const [inputs, setInputs] = useState("");
  const [headcount, setHeadcount] = useState("");
  const [repetitiveHours, setRepetitiveHours] = useState("");
  const [laborCost, setLaborCost] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", business_name: "" });

  const next = () => {
    if (step === 0 && !focus) {
      toast.error("Select what you're trying to improve");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = contactSchema.safeParse(contact);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Please complete the form");
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("roi_leads").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone || null,
      business_name: parsed.data.business_name || null,
      industry: focus,
      interest: `N.E.I.L. Assessment — ${focus}`,
      facility_size: [facilitySize, environment].filter(Boolean).join(" | ").slice(0, 500) || null,
      monthly_labor_cost: laborCost ? Number(laborCost) : null,
      repetitive_hours: repetitiveHours ? Number(repetitiveHours) : null,
    });
    setLoading(false);
    if (error) {
      toast.error("Could not submit. Please try again or call (336) 962-4539.");
      return;
    }
    setDone(true);
  };

  const field = "bg-background/60 border-primary/30";

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Start Your N.E.I.L.™ Assessment | Neil AI Global Solutions"
        description="Begin structured discovery with the N.E.I.L.™ Method. Tell us what you're trying to improve and Neil AI will review your operation before recommending automation."
        keywords="N.E.I.L. assessment, automation assessment, robotics discovery, operational assessment, RaaS assessment"
      />
      <Navigation />

      <main className="px-4 pt-32 md:pt-40">
        <div className="mx-auto max-w-3xl pb-20">
          <div className="text-center">
            <p className="mb-3 text-xs font-bold tracking-[0.32em] text-[#D6A84F]">DISCOVERY BEFORE PRESCRIPTION</p>
            <h1 className="font-orbitron text-3xl font-bold text-foreground md:text-5xl">
              Start Your N.E.I.L.™ Assessment
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
              A short, structured discovery request. No robot is recommended until we understand the operation.
            </p>
          </div>

          {done ? (
            <Card className="mt-10 border-primary/25 bg-card/60 p-8 text-center">
              <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
              <h2 className="font-orbitron mt-5 text-2xl font-bold text-foreground">DISCOVERY REQUEST RECEIVED</h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
                A Neil AI specialist will review the information you provided before recommending next steps.
                If additional discovery is required, we will schedule an operational or site assessment to
                complete the N.E.I.L.™ process.
              </p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button asChild size="lg" className="min-h-12 rounded-full px-8">
                  <Link to="/neil-method">Learn About The N.E.I.L.™ Method</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="min-h-12 rounded-full px-8">
                  <Link to="/raas">Explore RaaS</Link>
                </Button>
              </div>
            </Card>
          ) : (
            <>
              <ol className="mt-10 grid grid-cols-5 gap-2" aria-label="Assessment progress">
                {STEPS.map((s, i) => (
                  <li key={s.key} className="text-center">
                    <div
                      className={`font-orbitron mx-auto flex h-10 w-10 items-center justify-center rounded-full border text-sm font-bold ${
                        i <= step
                          ? "border-[#D6A84F]/60 bg-[#D6A84F]/15 text-[#D6A84F]"
                          : "border-[hsl(var(--line))] text-muted-foreground"
                      }`}
                    >
                      {s.key}
                    </div>
                    <p className="mt-2 hidden text-[11px] leading-tight text-muted-foreground sm:block">{s.label}</p>
                  </li>
                ))}
              </ol>

              <Card className="mt-6 border-primary/20 bg-card/60 p-6 md:p-8">
                {step === 0 && (
                  <div>
                    <h2 className="font-orbitron text-xl font-bold text-foreground">What are you trying to improve?</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      We start with the operational need — not a predetermined robot.
                    </p>
                    <div className="mt-5 grid gap-2 sm:grid-cols-3">
                      {FOCUS_AREAS.map((area) => (
                        <button
                          key={area}
                          type="button"
                          onClick={() => setFocus(area)}
                          aria-pressed={focus === area}
                          className={`min-h-12 rounded-2xl border px-4 py-3 text-sm font-medium transition-colors ${
                            focus === area
                              ? "border-primary bg-primary/15 text-foreground"
                              : "border-[hsl(var(--line))] bg-background/50 text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {area}
                        </button>
                      ))}
                    </div>
                    <div className="mt-5">
                      <Label>Describe the operational problem (optional)</Label>
                      <Textarea
                        value={need}
                        onChange={(e) => setNeed(e.target.value)}
                        placeholder="Bottlenecks, staffing challenges, repetitive work, service-level requirements…"
                        className={`mt-1.5 ${field}`}
                      />
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-4">
                    <h2 className="font-orbitron text-xl font-bold text-foreground">Where will automation operate?</h2>
                    <p className="text-sm text-muted-foreground">
                      Facility or site conditions determine what can realistically be deployed.
                    </p>
                    <div>
                      <Label>Facility size / acreage</Label>
                      <Input
                        value={facilitySize}
                        onChange={(e) => setFacilitySize(e.target.value)}
                        placeholder="e.g. 45,000 sq ft — or 12 acres"
                        className={`mt-1.5 ${field}`}
                      />
                    </div>
                    <div>
                      <Label>Environment details</Label>
                      <Textarea
                        value={environment}
                        onChange={(e) => setEnvironment(e.target.value)}
                        placeholder="Surfaces, elevators, doors, hallways, loading areas, traffic patterns, operating hours, terrain, slopes, network, safety or access restrictions…"
                        className={`mt-1.5 ${field}`}
                      />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <h2 className="font-orbitron text-xl font-bold text-foreground">
                      What resources and processes exist today?
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Share verified figures where possible — we won't estimate what can be confirmed.
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label>Headcount on this work</Label>
                        <Input value={headcount} onChange={(e) => setHeadcount(e.target.value)} className={`mt-1.5 ${field}`} />
                      </div>
                      <div>
                        <Label>Repetitive hours per week</Label>
                        <Input
                          type="number"
                          min={0}
                          value={repetitiveHours}
                          onChange={(e) => setRepetitiveHours(e.target.value)}
                          className={`mt-1.5 ${field}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Equipment, shifts, task frequency, existing technology</Label>
                      <Textarea value={inputs} onChange={(e) => setInputs(e.target.value)} className={`mt-1.5 ${field}`} />
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4">
                    <h2 className="font-orbitron text-xl font-bold text-foreground">
                      What is your current operating baseline?
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Where labor economics are relevant, we confirm a fully loaded baseline before any ROI
                      comparison. Estimates are labeled as estimates.
                    </p>
                    <div>
                      <Label>Current monthly labor / operating cost ($)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={laborCost}
                        onChange={(e) => setLaborCost(e.target.value)}
                        placeholder="Wages, burden, benefits, overtime, contractors"
                        className={`mt-1.5 ${field}`}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Not sure yet? Leave it blank — we'll establish the baseline together during discovery.
                    </p>
                  </div>
                )}

                {step === 4 && (
                  <form onSubmit={submit} className="space-y-4">
                    <h2 className="font-orbitron text-xl font-bold text-foreground">Where should we send the review?</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          required
                          value={contact.name}
                          onChange={(e) => setContact({ ...contact, name: e.target.value })}
                          className={`mt-1.5 ${field}`}
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          required
                          value={contact.email}
                          onChange={(e) => setContact({ ...contact, email: e.target.value })}
                          className={`mt-1.5 ${field}`}
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={contact.phone}
                          onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                          className={`mt-1.5 ${field}`}
                        />
                      </div>
                      <div>
                        <Label>Organization</Label>
                        <Input
                          value={contact.business_name}
                          onChange={(e) => setContact({ ...contact, business_name: e.target.value })}
                          className={`mt-1.5 ${field}`}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      By submitting, you consent to Neil AI Global Solutions contacting you about this
                      assessment. We do not sell your information.
                    </p>
                    <Button
                      type="submit"
                      disabled={loading}
                      size="lg"
                      className="h-12 w-full rounded-full bg-gradient-to-r from-[hsl(43_75%_55%)] to-[hsl(43_85%_65%)] font-semibold text-black"
                    >
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {loading ? "Submitting…" : "SUBMIT DISCOVERY REQUEST"}
                    </Button>
                  </form>
                )}

                <div className="mt-6 flex items-center justify-between">
                  <Button type="button" variant="ghost" onClick={back} disabled={step === 0} className="rounded-full">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  {step < STEPS.length - 1 && (
                    <Button type="button" onClick={next} className="rounded-full px-6">
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NeilAssessment;
